import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { listAccounts, listOperations } from '../../api/endpoints';
import { toApiPage, toApiSort, type GridPaginationModel } from '../../api/paging';
import { queryKeys } from '../../api/queryKeys';
import {
  isOperationSettled,
  operationSortKeys,
  sendOperationStatuses,
  type SendOperationListItem,
  type SendOperationStatus,
} from '../../api/types';
import { FlowisAvailabilityBanner } from '../../components/FlowisAvailabilityBanner';
import { ErrorBlock, GridEmptyOverlay } from '../../components/QueryStateOverlay';
import { GateStatusChip, OperationStatusChip } from '../../components/StatusChips';
import { operationStatusLabelKeys } from '../../components/statusLabels';
import { useTranslation } from 'react-i18next';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { accountLabel, formatDateTime } from '../../utils/format';
import { SendDialog } from './SendDialog';

/** Anything still queued is waiting on the background relay, so the list keeps asking. */
const queuedPollIntervalMs = 5000;

/**
 * Once delivered, the status still moves — the server's poller follows the document at Flowis and
 * writes what it learns. Slower than the queued interval: nothing here is waiting on a request.
 */
const openPollIntervalMs = 30000;

export function OperationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();
  // Nothing to query: an administrator has not picked a tenant yet, or the caller holds none.
  const hasNoTenant = selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'submittedAt', sort: 'desc' }]);
  const [statuses, setStatuses] = useState<SendOperationStatus[]>([]);
  const [participantId, setParticipantId] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState<Dayjs | null>(null);
  const [submittedTo, setSubmittedTo] = useState<Dayjs | null>(null);
  // ?send opens the dialog straight away — the files page links here with it.
  const [searchParams] = useSearchParams();
  const [sendOpen, setSendOpen] = useState(() => searchParams.has('send'));

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty list.
  const accountsQuery = useQuery({
    queryKey: queryKeys.accounts.list(selectedTenantGid),
    queryFn: ({ signal }) => listAccounts(signal),
    enabled: selectedTenantGid !== null,
  });

  const filter = useMemo(
    () => ({
      page: toApiPage(paginationModel.page),
      pageSize: paginationModel.pageSize,
      ...toApiSort(sortModel, operationSortKeys),
      ...(statuses.length > 0 ? { statuses } : {}),
      ...(participantId ? { participantId } : {}),
      // The picker names a day, the filter compares instants — both bounds are local-day edges,
      // the upper one inclusive.
      ...(submittedFrom?.isValid() ? { submittedFrom: submittedFrom.startOf('day').toISOString() } : {}),
      ...(submittedTo?.isValid() ? { submittedTo: submittedTo.endOf('day').toISOString() } : {}),
    }),
    [paginationModel, sortModel, statuses, participantId, submittedFrom, submittedTo],
  );

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty list.
  const query = useQuery({
    queryKey: queryKeys.operations.list(selectedTenantGid, filter),
    queryFn: ({ signal }) => listOperations(filter, signal),
    placeholderData: keepPreviousData,
    enabled: selectedTenantGid !== null,
    refetchInterval: (q) => {
      const items = q.state.data?.items ?? [];

      if (items.some((item) => item.status === 'Queued')) return queuedPollIntervalMs;

      return items.some((item) => !isOperationSettled(item)) ? openPollIntervalMs : false;
    },
  });

  const columns = useMemo<GridColDef<SendOperationListItem>[]>(
    () => [
      {
        field: 'status',
        headerName: t('operations.columns.status'),
        width: 130,
        // A recorded error on a still-queued operation means delivery has been tried and is being
        // retried — the relay writes it while the gate is out of reach.
        renderCell: (params) => (
          <OperationStatusChip status={params.row.status} retrying={!!params.row.errorCode} />
        ),
      },
      {
        field: 'documentFileName',
        headerName: t('operations.columns.document'),
        flex: 1,
        minWidth: 200,
        sortable: false,
      },
      { field: 'accountName', headerName: t('operations.columns.account'), width: 160, sortable: false },
      {
        field: 'flowisInvoiceId',
        headerName: t('operations.columns.invoiceNo'),
        width: 140,
        sortable: false,
      },
      {
        field: 'flowisStatus',
        headerName: t('operations.columns.flowisStatus'),
        width: 150,
        renderCell: (params) =>
          params.row.flowisStatus ? <GateStatusChip status={params.row.flowisStatus} /> : '—',
      },
      { field: 'errorCode', headerName: t('operations.columns.error'), width: 150, sortable: false },
      {
        field: 'submittedAt',
        headerName: t('operations.columns.submitted'),
        width: 170,
        valueGetter: (_value, row) => formatDateTime(row.submittedAt),
      },
      {
        field: 'lastCheckedAt',
        headerName: t('operations.columns.lastChecked'),
        width: 170,
        valueGetter: (_value, row) => formatDateTime(row.lastCheckedAt),
      },
    ],
    [t],
  );

  const noRowsOverlay = useCallback(
    () => <GridEmptyOverlay message={hasNoTenant ? t(noTenantKey) : t('operations.empty')} />,
    [hasNoTenant, noTenantKey, t],
  );

  return (
    <Stack spacing={2}>
      <FlowisAvailabilityBanner />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5">{t('operations.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('operations.description')}
          </Typography>
        </Box>
        <Tooltip title={hasNoTenant ? t(noTenantKey) : ''}>
          <span>
            <Button
              startIcon={<SendIcon />}
              variant="contained"
              disabled={hasNoTenant}
              onClick={() => setSendOpen(true)}
            >
              {t('operations.sendDocument')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Select
            size="small"
            multiple
            displayEmpty
            value={statuses}
            onChange={(event) => {
              const value = event.target.value;
              // The "any status" row carries the empty value; picking it clears the whole selection,
              // the same way the sibling single-selects reset through their "any" item.
              setStatuses(typeof value === 'string' || (value as string[]).includes('') ? [] : value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            renderValue={(selected) =>
              selected.length === 0
                ? t('operations.filters.anyStatus')
                : selected.map((status) => t(operationStatusLabelKeys[status])).join(', ')
            }
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">
              <Checkbox checked={statuses.length === 0} size="small" />
              <ListItemText primary={t('operations.filters.anyStatus')} />
            </MenuItem>
            {sendOperationStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={statuses.includes(status)} size="small" />
                <ListItemText primary={t(operationStatusLabelKeys[status])} />
              </MenuItem>
            ))}
          </Select>

          <TextField
            select
            size="small"
            label={t('operations.filters.account')}
            value={participantId}
            onChange={(event) => {
              setParticipantId(event.target.value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            error={accountsQuery.isError}
            helperText={accountsQuery.isError ? t('operations.filters.accountsFailed') : undefined}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('operations.filters.anyAccount')}</MenuItem>
            {/* Filtered by the address the account sends under, which is also how it was named on
                the way in — an account without one has nothing to have sent. */}
            {(accountsQuery.data ?? [])
              .filter((account) => account.participantId)
              .map((account) => (
                <MenuItem key={account.accountGid} value={account.participantId!}>
                  {accountLabel(account)}
                </MenuItem>
              ))}
          </TextField>

          <DatePicker
            label={t('operations.filters.submittedFrom')}
            value={submittedFrom}
            onChange={(value) => {
              setSubmittedFrom(value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
          <DatePicker
            label={t('operations.filters.submittedTo')}
            value={submittedTo}
            onChange={(value) => {
              setSubmittedTo(value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
        </Stack>
      </Paper>

      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      <Paper sx={{ height: 620 }}>
        <DataGrid<SendOperationListItem>
          rows={hasNoTenant ? [] : (query.data?.items ?? [])}
          columns={columns}
          getRowId={(row) => row.operationGid}
          loading={query.isFetching}
          rowCount={hasNoTenant ? 0 : (query.data?.pagedMetadata.totalCount ?? 0)}
          paginationMode="server"
          sortingMode="server"
          disableColumnFilter
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ noRowsOverlay }}
          onRowClick={(params) => void navigate(`/operations/${params.row.operationGid}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>

      {sendOpen && <SendDialog onClose={() => setSendOpen(false)} />}
    </Stack>
  );
}
